// Native bare/tray outcomes: draw faces, decide totals and detail, then send the decided row to the lazy save.
// Existing recorded replay inputs: tools/test/fixtures/rolls-dice-oracle.json.

export const ROLL_SCHEMA = 'janus.roll.truth.v1';
export type DiceMeta = { roll_order?: number; kept: boolean };
export type DiceTuple = [number, string] | [number, string, DiceMeta];
/** A die source: returns a face in [1, die]. Math.random by default — PHP itself uses rand() here, not a CSPRNG. */
export type DieSource = (die: number) => number;
export const mathRandomDie: DieSource = die => Math.floor(Math.random() * Math.max(1, die)) + 1;

/** roll_dice_arr()'s draw count: 0 for the modifier-only branch, else dice × (|adv| + 1). */
export function faceCount(dice: number, die: number, adv: number): number {
  return dice <= 0 || die <= 0 ? 0 : dice * (Math.abs(adv) + 1);
}
export function drawFaces(dice: number, die: number, adv: number, source: DieSource = mathRandomDie): number[] {
  return Array.from({ length: faceCount(dice, die, adv) }, () => source(die));
}

/** PHP's element-wise array comparison for [int, string] tuples (rsort's comparator): face, then label bytes. */
function phpTupleCompare(a: DiceTuple, b: DiceTuple): number {
  if (a[0] !== b[0]) return a[0] < b[0] ? -1 : 1;
  return a[1] === b[1] ? 0 : a[1] < b[1] ? -1 : 1;
}

/** roll_dice_arr($dice, $die, $mod, $adv) over `faces` (draw order). Null when the faces do not fit the roll. */
export function buildRollDiceArr(dice: number, die: number, mod: number, adv: number, faces: readonly number[]): DiceTuple[] | null {
  if (dice < 0 || die < 0 || faces.length !== faceCount(dice, die, adv)) return null;
  if (faces.some(f => !Number.isInteger(f) || f < 1 || f > die)) return null;
  if (dice === 0 || die === 0) return [[mod, 'roll_result (modifier only)']];
  let next = 0;
  let roll = 0;
  const out: DiceTuple[] = [];
  if (adv === 0) {
    const flat: DiceTuple[] = [];
    for (let x = 1; x <= dice; x++) { const f = faces[next++]!; flat.push([f, `d${die}_${x}`]); roll += f; }
    flat.sort((a, b) => phpTupleCompare(b, a));
    for (const t of flat) out.push([t[0], t[1], { kept: true }]);
  } else {
    const type = adv > 0 ? 'ADV' : 'DIS';
    const stacks = Math.abs(adv);
    for (let x = 1; x <= dice; x++) {
      const pair: [number, string, DiceMeta][] = [];
      for (let r = 1; r <= stacks + 1; r++) pair.push([faces[next++]!, `d${die} ${type}${stacks}`, { roll_order: r, kept: false }]);
      pair.sort(adv > 0 ? (a, b) => b[0] - a[0] : (a, b) => a[0] - b[0]); // stable, like PHP 8 usort
      pair.forEach((t, i) => { t[2] = { roll_order: t[2].roll_order, kept: i === 0 }; });
      out.push(...pair);
      roll += pair[0]![0];
    }
  }
  roll += mod;
  return [[roll, 'roll_result'], [mod, 'sum_mod'], ...out];
}

// PHP's numeric-string grammar (PHP 8): ASCII whitespace only (space, \t \n \r \v \f), an optional sign, digits with an optional
// fraction and exponent. A NUMERIC string may carry trailing whitespace; intval() of a non-numeric string reads its LEADING numeric
// prefix ("1.9e1abc" → 19, ".9e2abc" → 90). Codex review 2026-09-28: the \s class admitted a non-breaking space PHP rejects.
const PHP_NUM = String.raw`[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?`;
const PHP_WS = String.raw`[ \t\n\r\v\f]*`;
const PHP_NUMERIC = new RegExp(`^${PHP_WS}${PHP_NUM}${PHP_WS}$`);
const PHP_PREFIX = new RegExp(`^${PHP_WS}(${PHP_NUM})`);
/** PHP intval() of a posted scalar. */
export function phpIntval(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : 0;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const m = PHP_PREFIX.exec(String(value ?? ''));
  if (!m) return 0;
  const n = Math.trunc(Number(m[1]));
  return Number.isFinite(n) ? n : 0;
}
export const isPhpNumeric = (value: unknown): boolean => typeof value === 'number' ? Number.isFinite(value) : PHP_NUMERIC.test(String(value ?? ''));
/**
 * get_data() runs every posted string through mysql_safe_ajax() (trim, urldecode, strip_tags) BEFORE the endpoint reads it, so an
 * input that normalization would change ("2%30" → "20", " random_table ") is refused by the caller before posting.
 */
const legacyNormalizes = (value: unknown): boolean => {
  if (value === null || value === undefined || typeof value === 'number' || typeof value === 'boolean') return false;
  const s = String(value);
  return s !== s.trim() || /[%+<>\\\u0000]/.test(s);
};
/** The most faces one click may draw (the commit's payload cap is sized for it). */
export const MAX_FACES = 1000;

export interface BareRollInput { roll_type_1: unknown; roll_type_2: unknown; roll_item_id: unknown; dice: unknown; die: unknown; roll_mod: unknown; adv_dis_stacks: unknown }
export interface BareRoll { dice: number; die: number; roll_mod: number; adv_dis_stacks: number; faces: number[]; roll_result: number; rolls_full: string }
/** Random-table kinds run their own subsystem (random_table_roll): never a bare roll. */
export const RANDOM_TABLE_KINDS: ReadonlySet<string> = new Set(['random_table', 'random_loot_table', 'random_encounter_table', 'random_name_table']);

/**
 * Bare-roll arithmetic. Null = unsupported input; the caller displays the reason without posting.
 */
export function resolveBareRoll(input: BareRollInput, source: DieSource = mathRandomDie): BareRoll | null {
  if ([input.roll_type_1, input.roll_type_2, input.roll_item_id, input.dice, input.die, input.roll_mod, input.adv_dis_stacks].some(legacyNormalizes)) return null;
  if (RANDOM_TABLE_KINDS.has(String(input.roll_type_1 ?? ''))) return null;
  // roll_inc.php: a bracketed variable tag zeroes the field; then every field must be numeric.
  const zeroTag = (v: unknown): unknown => String(v ?? '').includes('[') ? 0 : v;
  const diceRaw = zeroTag(input.dice), dieRaw = zeroTag(input.die);
  if (!isPhpNumeric(diceRaw) || !isPhpNumeric(dieRaw)) return null;
  const dice = phpIntval(diceRaw), die = phpIntval(dieRaw);
  const mod = phpIntval(input.roll_mod), adv = phpIntval(input.adv_dis_stacks);
  if (dice < 0 || die < 0 || dice > 100 || die > 10000 || Math.abs(adv) > 10 || Math.abs(mod) > 100000) return null;
  if (faceCount(dice, die, adv) > MAX_FACES) return null;
  const faces = drawFaces(dice, die, adv, source);
  const arr = buildRollDiceArr(dice, die, mod, adv, faces);
  if (!arr) return null;
  return { dice, die, roll_mod: mod, adv_dis_stacks: adv, faces, roll_result: arr[0]![0], rolls_full: JSON.stringify(arr.slice(2)) };
}

export const COMPLEX_DIE = [4, 6, 8, 10, 12, 20] as const;
export interface ComplexRoll { counts: Record<string, number>; mod: number; adv: number; faces: Record<string, number[]>; dice: number; die: number; roll_result: number; rolls_full: string }
/**
 * roll_complex_dice_inc.php's tray (the modifier added ONCE — Lane ROLLS decision 2026-09-27). With no dice and no modifier
 * the tray rolls a plain d20 whose faces are not listed (legacy). Null = unsupported input (a non-integer / negative quantity).
 */
export function resolveComplexRoll(qty: Record<number, unknown>, modRaw: unknown, advRaw: unknown, source: DieSource = mathRandomDie): ComplexRoll | null {
  if ([...COMPLEX_DIE.map(d => qty[d]), modRaw, advRaw].some(legacyNormalizes)) return null;
  const counts: Record<string, number> = {};
  for (const d of COMPLEX_DIE) {
    const raw = String(qty[d] ?? '').trim();
    if (raw !== '' && !/^\d+$/.test(raw)) return null;
    const n = raw === '' ? 0 : parseInt(raw, 10);
    if (n > 100) return null;
    counts[d] = n;
  }
  const mod = phpIntval(modRaw), adv = phpIntval(advRaw);
  if (Math.abs(adv) > 10 || Math.abs(mod) > 100000) return null;
  const total = COMPLEX_DIE.reduce((s, d) => s + counts[d]!, 0);
  if ((total === 0 ? 1 : total) * (Math.abs(adv) + 1) > MAX_FACES) return null;
  const faces: Record<string, number[]> = { d0: [] };
  let dice = 0, die = 0, result = 0;
  if (total === 0 && mod === 0) {
    dice = 1; die = 20;
    faces.d0 = drawFaces(1, 20, adv, source);
    result = buildRollDiceArr(1, 20, 0, adv, faces.d0)![0]![0];
  } else {
    result = mod;
  }
  const full: DiceTuple[] = [];
  for (const d of COMPLEX_DIE) {
    if (!counts[d]) continue;
    const f = drawFaces(counts[d]!, d, adv, source);
    faces[String(d)] = f;
    const arr = buildRollDiceArr(counts[d]!, d, 0, adv, f)!;
    result += arr[0]![0];
    full.push(...arr.slice(2));
  }
  return { counts, mod, adv, faces, dice, die, roll_result: result, rolls_full: JSON.stringify(full) };
}
